import { Component } from "react";
import { SHEET_AUTO_CLOSE_MS } from "../constants";
import { AUDIO_MODE_PROPS } from "./audio-modes";
import { popupComponentDefaultProps } from "./default-props";
import ActionsFooter from "./actions-footer";
import DebugBar from "./debug-bar";
import EffectsSection from "./effects-section";
import PopupHeader from "./popup-header";
import Mood from "./mood";
import SkinSheet from "./skin-sheet";
import SkinBackdrop from "./skin-backdrop";
import HelpSheet from "./help-sheet";
import FeedbackSheet from "./feedback-sheet";
import SlowControlSection from "./slow-control-section";
import {
  hasShuffleSyncPropsChanged,
  seedSliderTouchedKeys,
  sliderFillPct,
  sliderMidValue,
  sliderValueFromClientX,
} from "./tile-slider";

const TILE_VALUE_HOLD_MS = 300;
const TILE_VALUE_FADE_MS = 300;

class PopupComponent extends Component {
  constructor(props) {
    super(props);

    this.sliderTouched = {};
    this.tileDrag = null;
    this.suppressAdjustableClick = false;
    this.state = {
      mount: false,
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: null,
      releasingTileKey: null,
      fillGrowingKeys: {},
      shuffleBusy: false,
      subscriptionStatusStep: 0,
      openSheet: null,
      contentPushHeight: null,
      moodScrimTop: null,
      moodScrimOpacity: 0,
    };
    this.contentPushRef = null;
    this.contentEffectsRef = null;
  }

  componentDidMount() {
    this.syncContentPushHeight();
    this.contentEffectsResizeObserver = new ResizeObserver(() =>
      this.syncContentPushHeight(),
    );
    if (this.contentEffectsRef) {
      this.contentEffectsResizeObserver.observe(this.contentEffectsRef);
    }
    setTimeout(() => this.setState({ mount: true }), 200);
    this.scheduleSubscriptionStatusRotation();
    this.seedSliderTouched(this.props);
  }

  componentDidUpdate(prevProps, prevState) {
    this.componentDidUpdateSheet(prevState);

    const statusChanged =
      prevProps.trialMode !== this.props.trialMode ||
      prevProps.remainingTrialDays !== this.props.remainingTrialDays ||
      prevProps.isPro !== this.props.isPro ||
      prevProps.usageAvailable !== this.props.usageAvailable ||
      prevProps.trialEnded !== this.props.trialEnded ||
      prevProps.usageHit !== this.props.usageHit;
    if (statusChanged) this.scheduleSubscriptionStatusRotation();

    AUDIO_MODE_PROPS.forEach(({ key, prop, isActive }) => {
      const wasActive = isActive ? isActive(prevProps) : prevProps[prop];
      const isActiveNow = isActive ? isActive(this.props) : this.props[prop];
      if (!wasActive && isActiveNow) {
        if (!this.state.fillGrowingKeys[key]) {
          this.startFillGrow(key);
        }
      } else if (wasActive && !isActiveNow) {
        this.clearFillGrow(key);
      }
    });

    if (hasShuffleSyncPropsChanged(prevProps, this.props)) {
      this.seedSliderTouched(this.props);
    }
  }

  componentDidUpdateSheet(prevState) {
    if (prevState.openSheet !== this.state.openSheet) {
      this.armSheetAutoClose();
    }
  }

  startFillGrow(key) {
    this.setState(
      (state) => ({
        fillGrowingKeys: { ...state.fillGrowingKeys, [key]: true },
      }),
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.clearFillGrow(key);
          });
        });
      },
    );
  }

  clearFillGrow(key) {
    if (!this.state.fillGrowingKeys[key]) return;
    this.setState((state) => {
      const fillGrowingKeys = { ...state.fillGrowingKeys };
      delete fillGrowingKeys[key];
      return { fillGrowingKeys };
    });
  }

  componentWillUnmount() {
    clearTimeout(this.subscriptionStatusTimer);
    clearTimeout(this.releaseTileTimer);
    clearTimeout(this.sheetAutoCloseTimer);
    clearTimeout(this.sheetCloseFallbackTimer);
    this.contentEffectsResizeObserver?.disconnect();
    this.teardownMoodScroll();
    this.unmounted = true;
    this.tileDrag = null;
  }

  // Remember .content__effects height while no sheet is open. The pin is
  // applied only after a sheet opens so a short measurement cannot clip
  // Ambient Effects (the last row) against overflow:hidden.
  syncContentPushHeight() {
    if (this.state.openSheet) return;
    const next = this.contentEffectsRef?.offsetHeight;
    if (!next) return;
    if (this.state.contentPushHeight === next) return;
    this.setState({ contentPushHeight: next });
  }

  teardownMoodScroll() {
    this.sheetScrollGen = (this.sheetScrollGen || 0) + 1;
    this.sheetOverflowObserver?.disconnect();
    this.sheetOverflowObserver = null;
    (this.sheetScrollRetryTimers || []).forEach(clearTimeout);
    this.sheetScrollRetryTimers = [];
    const el = this.contentPushRef;
    clearTimeout(this.moodScrollFallbackTimer);
    this.moodScrollFallbackTimer = null;
    if (!el) return;
    if (this.onMoodScroll) {
      el.removeEventListener("scroll", this.onMoodScroll);
      this.onMoodScroll = null;
    }
    if (this.onMoodScrollEnd) {
      el.removeEventListener("scrollend", this.onMoodScrollEnd);
      this.onMoodScrollEnd = null;
    }
  }

  syncMoodScrimFromScroll() {
    if (this.unmounted) return;
    const el = this.contentPushRef;
    if (!el) return;

    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const progress =
      max <= 0
        ? this.state.openSheet
          ? 1
          : 0
        : Math.min(1, Math.max(0, el.scrollTop / max));

    const root = document.getElementById("Slowed");
    const mood = document.getElementById("mood-sheet");
    let top = this.state.moodScrimTop;
    if (root && mood && progress > 0) {
      top = Math.max(
        0,
        Math.round(
          mood.getBoundingClientRect().top - root.getBoundingClientRect().top,
        ),
      );
    } else if (progress <= 0) {
      top = null;
    }

    if (
      top !== this.state.moodScrimTop ||
      progress !== this.state.moodScrimOpacity
    ) {
      this.setState({ moodScrimTop: top, moodScrimOpacity: progress });
    }
  }

  // Idle sheets close themselves; any pointer/key activity inside restarts
  // the countdown so a sheet in use is never yanked away.
  armSheetAutoClose() {
    clearTimeout(this.sheetAutoCloseTimer);
    if (!this.state.openSheet) return;
    this.sheetAutoCloseTimer = setTimeout(() => {
      if (!this.unmounted && this.state.openSheet) this.setSheetOpen(null);
    }, SHEET_AUTO_CLOSE_MS);
  }

  // `kind` is one of "mood" | "skin" | "help" | "feedback", or null to close.
  setSheetOpen(kind) {
    this.teardownMoodScroll();
    clearTimeout(this.sheetAutoCloseTimer);
    // A close/swap already in flight must not land on the new sheet.
    clearTimeout(this.sheetCloseFallbackTimer);

    const el = this.contentPushRef;
    if (!el) {
      this.setState({ openSheet: kind || null });
      return;
    }

    const runScroll = (targetTop, onDone) => {
      // Opening mounts the sheet under a height pin + overflow clip. The
      // destination can still move if the sheet grows after layout, but
      // restarting a smooth scroll just because we have not arrived yet
      // makes the panel hitch at the start and again at the end.
      const gen = this.sheetScrollGen;
      const started = performance.now();
      const opening = targetTop !== 0;
      const deadline = opening ? 1200 : 800;
      let startedScroll = false;
      let aimedTop = null;

      const currentTop = () => {
        const push = this.contentPushRef;
        const max = Math.max(0, push.scrollHeight - push.clientHeight);
        return Math.max(0, Math.min(targetTop, max));
      };

      const scrollToTarget = (top) => {
        const push = this.contentPushRef;
        if (!push) return;
        if (aimedTop != null && Math.abs(aimedTop - top) < 1) return;
        aimedTop = top;
        clearTimeout(this.moodScrollFallbackTimer);
        this.moodScrollFallbackTimer = setTimeout(finish, 450);
        if (Math.abs(push.scrollTop - top) < 1) {
          finish();
          return;
        }
        push.scrollTo({ top, behavior: "smooth" });
      };

      const finish = () => {
        if (this.onMoodScrollEnd !== finish) return;
        const push = this.contentPushRef;
        const top = push ? currentTop() : 0;
        // Chase only when the destination itself moved (sheet grew), not
        // while a smooth scroll is still travelling toward the same target.
        if (
          opening &&
          push &&
          performance.now() - started < deadline &&
          aimedTop != null &&
          top > aimedTop + 1
        ) {
          scrollToTarget(top);
          return;
        }
        this.syncMoodScrimFromScroll();
        this.teardownMoodScroll();
        onDone?.();
      };

      const attempt = () => {
        if (this.unmounted || this.sheetScrollGen !== gen) return;
        const push = this.contentPushRef;
        if (!push) return;

        if (startedScroll) {
          scrollToTarget(currentTop());
          return;
        }

        const max = Math.max(0, push.scrollHeight - push.clientHeight);
        if (opening && max <= 0) {
          if (performance.now() - started < deadline) {
            requestAnimationFrame(attempt);
          }
          return;
        }

        startedScroll = true;
        this.onMoodScroll = () => this.syncMoodScrimFromScroll();
        this.onMoodScrollEnd = finish;
        push.addEventListener("scroll", this.onMoodScroll);
        push.addEventListener("scrollend", this.onMoodScrollEnd);
        scrollToTarget(Math.max(0, Math.min(targetTop, max)));
      };

      if (opening && this.contentPushRef) {
        this.sheetOverflowObserver = new ResizeObserver(() => attempt());
        this.sheetOverflowObserver.observe(this.contentPushRef);
        const sheet = document.getElementById("mood-sheet");
        if (sheet) this.sheetOverflowObserver.observe(sheet);
      }
      this.sheetScrollRetryTimers = [32, 80, 160, 320, 480].map((ms) =>
        setTimeout(attempt, ms),
      );
      requestAnimationFrame(attempt);
    };

    const openSheet = (next) => {
      this.setState({ openSheet: next }, () => {
        runScroll(Number.POSITIVE_INFINITY);
      });
    };

    // Runs `after` once the panel is back at the top, or on a hard fallback
    // if scrollend never lands — the sheet must never get stuck part-way.
    const rewindThen = (after) => {
      let done = false;
      const finish = () => {
        if (done || this.unmounted) return;
        done = true;
        clearTimeout(this.sheetCloseFallbackTimer);
        after();
      };
      this.sheetCloseFallbackTimer = setTimeout(finish, 600);
      runScroll(0, finish);
    };

    if (!kind) {
      // Scroll up first while layout stays put, then clear open state.
      rewindThen(() => this.setState({ openSheet: null }));
      return;
    }

    // Switching sheets rides the same rewind-then-open motion. Swapping the
    // content in place while the panel sits at the bottom makes it jump: the
    // new sheet is a different height, so the browser clamps scrollTop and
    // the second scroll starts from somewhere the user never scrolled to.
    // `openSheet` stays non-null throughout, so the height pin never drops.
    if (this.state.openSheet && this.state.openSheet !== kind) {
      rewindThen(() => openSheet(kind));
      return;
    }

    openSheet(kind);
  }

  seedSliderTouched(props) {
    Object.assign(this.sliderTouched, seedSliderTouchedKeys(props));
  }

  clearTileValueRevealTimer() {
    clearTimeout(this.releaseTileTimer);
    this.releaseTileTimer = null;
  }

  startTileValueReveal(modeKey) {
    this.clearTileValueRevealTimer();
    this.setState({
      draggingTileKey: null,
      holdingTileKey: null,
      revealingTileKey: modeKey,
      releasingTileKey: null,
    });
    this.releaseTileTimer = setTimeout(() => {
      if (this.unmounted) return;
      this.setState({
        revealingTileKey: null,
        releasingTileKey: modeKey,
      });
      this.releaseTileTimer = setTimeout(() => {
        if (this.unmounted) return;
        this.setState({ releasingTileKey: null });
      }, TILE_VALUE_FADE_MS);
    }, TILE_VALUE_HOLD_MS);
  }

  finishTileInteraction(modeKey, { animateRelease = false } = {}) {
    this.tileDrag = null;
    this.clearTileValueRevealTimer();

    const shouldAnimate =
      animateRelease &&
      modeKey &&
      (this.state.holdingTileKey === modeKey ||
        this.state.draggingTileKey === modeKey ||
        this.state.revealingTileKey === modeKey);

    if (shouldAnimate) {
      this.startTileValueReveal(modeKey);
      return;
    }

    if (
      this.state.draggingTileKey ||
      this.state.holdingTileKey ||
      this.state.revealingTileKey ||
      this.state.releasingTileKey
    ) {
      this.setState({
        draggingTileKey: null,
        holdingTileKey: null,
        revealingTileKey: null,
        releasingTileKey: null,
      });
    }
  }

  updateTileSliderFromPointer(clientX, mode, el) {
    if (!mode.slider || !el) return;

    const value = sliderValueFromClientX(clientX, el, mode.slider);
    mode.slider.onChange(value);
    this.sliderTouched[mode.key] = true;
  }

  applyDefaultSliderIfNeeded(mode) {
    if (!mode.slider || this.sliderTouched[mode.key]) return;

    const defaultVal =
      mode.slider.defaultValue !== undefined
        ? mode.slider.defaultValue
        : sliderMidValue(mode.slider);
    mode.slider.onChange(defaultVal);
    this.sliderTouched[mode.key] = true;
  }

  onAdjustableTilePointerDown(event, mode) {
    if (this.props.isProFeaturesLocked) return;

    const wasActive = !!mode.active;

    this.clearTileValueRevealTimer();
    this.tileDrag = {
      key: mode.key,
      pointerId: event.pointerId,
      startX: event.clientX,
      dragging: false,
      el: event.currentTarget,
      slider: mode.slider,
      wasActive,
      isActive: wasActive,
      activatedOnDown: false,
    };

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    if (!wasActive) {
      // Prefer click position; mid only when there is no pointer.
      const setFromClick = mode.slider && !this.sliderTouched[mode.key];
      mode.onToggle();
      if (setFromClick) {
        this.updateTileSliderFromPointer(
          event.clientX,
          mode,
          event.currentTarget,
        );
      }
      this.tileDrag.isActive = true;
      this.tileDrag.activatedOnDown = true;
      this.setState(
        {
          holdingTileKey: mode.key,
          revealingTileKey: null,
          releasingTileKey: null,
          fillGrowingKeys: {
            ...this.state.fillGrowingKeys,
            [mode.key]: true,
          },
        },
        () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.clearFillGrow(mode.key);
            });
          });
        },
      );
      return;
    }

    this.setState({
      holdingTileKey: mode.key,
      revealingTileKey: null,
      releasingTileKey: null,
    });
  }

  onAdjustableTilePointerMove(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;
    if (!this.tileDrag.isActive) return;

    if (Math.abs(event.clientX - this.tileDrag.startX) <= 4) return;

    if (!this.tileDrag.dragging) {
      this.tileDrag.dragging = true;
      this.clearFillGrow(this.tileDrag.key);
      if (this.state.draggingTileKey !== this.tileDrag.key) {
        this.setState({ draggingTileKey: this.tileDrag.key });
      }
    }

    this.updateTileSliderFromPointer(
      event.clientX,
      { key: this.tileDrag.key, slider: this.tileDrag.slider },
      this.tileDrag.el,
    );
  }

  onAdjustableTilePointerUp(event, mode) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { dragging, wasActive, activatedOnDown } = this.tileDrag;
    const shouldAnimateRelease =
      (dragging || activatedOnDown) &&
      this.state.holdingTileKey === mode.key;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore release failures in unsupported environments.
    }

    this.finishTileInteraction(mode.key, {
      animateRelease: shouldAnimateRelease,
    });

    this.suppressAdjustableClick = true;

    if (dragging) {
      // Empty end of the track turns the effect off.
      if (mode.active && sliderFillPct(mode.slider) <= 0) {
        mode.onToggle();
        delete this.sliderTouched[mode.key];
      }
      return;
    }

    if (wasActive) {
      mode.onToggle();
    }
  }

  onAdjustableTileClick(mode) {
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    if (this.suppressAdjustableClick) {
      this.suppressAdjustableClick = false;
      return;
    }

    this.handleAdjustableTileTap(mode);
  }

  handleAdjustableTileTap(mode) {
    if (mode.active) {
      mode.onToggle();
      return;
    }

    this.applyDefaultSliderIfNeeded(mode);
    mode.onToggle();
    this.flashTileValue(mode.key);
  }

  flashTileValue(modeKey) {
    this.startTileValueReveal(modeKey);
  }

  onAdjustableTilePointerCancel(event) {
    if (!this.tileDrag || event.pointerId !== this.tileDrag.pointerId) return;

    const { key, dragging, activatedOnDown } = this.tileDrag;

    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch (_error) {
      // Ignore capture failures in unsupported environments.
    }

    this.finishTileInteraction(key, {
      animateRelease:
        (dragging || activatedOnDown) && this.state.holdingTileKey === key,
    });
  }

  async runMood(mood) {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onApplyMood?.(mood);
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  async runClearMood() {
    if (this.state.shuffleBusy) return;
    if (this.props.isProFeaturesLocked) {
      this.props.onUpgradeClick();
      return;
    }

    this.setState({ shuffleBusy: true });
    try {
      return await this.props.onClearMood?.();
    } finally {
      this.setState({ shuffleBusy: false });
    }
  }

  scheduleSubscriptionStatusRotation() {
    clearTimeout(this.subscriptionStatusTimer);
    const {
      trialMode,
      remainingTrialDays,
      isPro,
      trialEnded,
    } = this.props;
    const hasTrial = trialMode && remainingTrialDays >= 0;
    // Usage mode rotates Unlock PRO → Usage mode → minutes left → Resets
    // weekly, including after the cap so 0 min left still cycles.
    const hasUsage = !hasTrial && trialEnded === true;
    const shouldRotate = !isPro && (hasTrial || hasUsage);
    if (!shouldRotate) {
      this.setState({ subscriptionStatusStep: 0 });
      return;
    }
    const dwell = hasUsage ? [4500, 2400, 2400, 2400] : [4500, 2400, 2400];
    const stepCount = dwell.length;
    let step = 0;
    this.setState({ subscriptionStatusStep: 0 });
    const tick = () => {
      step = (step + 1) % stepCount;
      this.setState({ subscriptionStatusStep: step });
      this.subscriptionStatusTimer = setTimeout(tick, dwell[step]);
    };
    this.subscriptionStatusTimer = setTimeout(tick, 2000);
  }

  render() {
    const {
      slowControl,
      isSlowedEnabled,
      error,
      notSupported,
      musicProOnly,
      isHeaderVisible,
      isPro,
      trialMode,
      trialEnded,
      skin,
      remainingTrialDays,
      usageHit,
      isFeedbackEnabled,
      isRateAppEnabled,
      isProFeaturesLocked,
      contentApplying,
      billingLoading,
    } = this.props;

    const { openSheet, contentPushHeight, moodScrimTop, moodScrimOpacity } =
      this.state;
    const moodOpen = openSheet !== null;
    const { debugMode: debugModeEnabled } = this.props;

    if (error) {
      return (
        <div className="popup">
          <div className="content">
            <p>
              {process.env.DEBUG === "true"
                ? error.message
                : "Something went wrong"}
            </p>
          </div>
        </div>
      );
    }

    const tileHandlers = {
      onAdjustablePointerDown: (event, mode) =>
        this.onAdjustableTilePointerDown(event, mode),
      onAdjustablePointerMove: (event) =>
        this.onAdjustableTilePointerMove(event),
      onAdjustablePointerUp: (event, mode) =>
        this.onAdjustableTilePointerUp(event, mode),
      onAdjustablePointerCancel: (event) =>
        this.onAdjustableTilePointerCancel(event),
      onAdjustableClick: (mode) => this.onAdjustableTileClick(mode),
    };

    const skinModeClass =
      {
        mono: "mono-mode",
        winamp: "winamp-mode",
        y2k: "y2k-mode",
        neoy2k: "neoy2k-mode",
        matrix: "matrix-mode",
        aquarium: "aquarium-mode",
        dracula: "dracula-mode",
        alien: "alien-mode",
        macos: "macos-mode",
      }[skin] ||
      null;
    // Fast Mode runs the controls backwards, so the interface runs backwards
    // with them. Pro-gated, so a lapsed subscriber never sees it.
    const speedUpReversed = !!this.props.speedUpMode && !isProFeaturesLocked;

    const skinClassName = [skinModeClass, moodOpen ? "mood-open" : null]
      .filter(Boolean)
      .join(" ");

    return (
      <div id="Slowed" className={skinClassName || undefined}>
        <SkinBackdrop skin={skin} />
        <div
          className="mood-panel-scrim"
          aria-hidden="true"
          style={{
            opacity: moodScrimOpacity,
            visibility: moodScrimOpacity > 0.01 ? "visible" : "hidden",
            ...(moodScrimTop != null ? { top: `${moodScrimTop}px` } : null),
          }}
        />
        <div
          className={`popup ${this.state.mount ? "" : "mounting"}${
            speedUpReversed ? " popup--reversed" : ""
          }${debugModeEnabled ? " popup--debug" : ""}`}
        >
          {isHeaderVisible && (
            <PopupHeader
              skin={skin}
              trackName={this.props.trackName}
              slowControl={slowControl}
              isPlaying={this.props.isPlaying}
              announcement={this.props.moodAnnouncement}
              isPro={isPro}
              trialMode={trialMode}
              trialEnded={trialEnded}
              remainingTrialDays={remainingTrialDays}
              usageHit={usageHit}
              usageRemaining={this.props.usageRemaining}
              subscriptionStatusStep={this.state.subscriptionStatusStep}
              notSupported={notSupported}
              musicProOnly={musicProOnly}
              isSlowedEnabled={isSlowedEnabled}
              contentApplying={contentApplying}
              mount={this.state.mount}
              billingLoading={billingLoading}
              upgradeError={this.props.upgradeError}
              onUpgradeClick={() => this.props.onUpgradeClick()}
              onUsageClick={() => this.props.onUsageClick()}
              onManageSubscriptionClick={() =>
                this.props.onManageSubscriptionClick()
              }
              onDebugToggle={() =>
                this.props.onDebugModeChange?.(!debugModeEnabled)
              }
              onSlowedToggle={(value) => this.props.onSlowedToggle(value)}
              hideManagePlan={this.props.hideManagePlan}
            />
          )}

          <div
            className={`content${musicProOnly ? " content--disabled" : ""}`}
          >
            {musicProOnly ? (
              <div className="content__overlay content__overlay--music-pro">
                <p className="content__overlay-title">YouTube Music</p>
                <p className="content__overlay-text">
                  Slowing tracks on{" "}
                  <strong>music.youtube.com</strong> is included with Slowed Pro.
                </p>
                <button
                  type="button"
                  className="content__overlay-cta"
                  onClick={() => this.props.onUpgradeClick()}
                >
                  Unlock Pro
                </button>
              </div>
            ) : null}

            <div
              className={`content__push-shell${moodOpen ? " content__push-shell--mood-open" : ""}`}
            >
              <div
                className="content__push"
                ref={(el) => {
                  this.contentPushRef = el;
                }}
                style={
                  moodOpen && contentPushHeight != null
                    ? { height: contentPushHeight }
                    : undefined
                }
              >
                <div className="content__effects-frame">
                  <div
                    className={`content__effects${!isSlowedEnabled && !musicProOnly ? " content--disabled" : ""}${moodOpen ? " content__effects--dimmed" : ""}`}
                    ref={(el) => {
                      this.contentEffectsRef = el;
                    }}
                    {...(moodOpen ? { inert: true } : {})}
                  >
                    {!isSlowedEnabled && !musicProOnly && (
                      <div className="content__overlay" />
                    )}

                    <SlowControlSection
                      slowControl={slowControl}
                      speedUpMode={
                        this.props.speedUpMode && !isProFeaturesLocked
                      }
                      onSlowControlChange={(value) =>
                        this.props.onSlowControlChange(value)
                      }
                    />

                    <EffectsSection
                      props={this.props}
                      tileState={this.state}
                      tileHandlers={tileHandlers}
                    />
                  </div>

                  {moodOpen && (
                    <button
                      type="button"
                      className="content__mood-backdrop"
                      aria-label="Close panel"
                      onClick={() => this.setSheetOpen(null)}
                    />
                  )}
                </div>

                <div
                  onPointerDown={() => this.armSheetAutoClose()}
                  onPointerMove={() => this.armSheetAutoClose()}
                  onKeyDown={() => this.armSheetAutoClose()}
                >
                {openSheet === "mood" && (
                  <Mood
                    open
                    onOpenChange={(open) => this.setSheetOpen(open ? "mood" : null)}
                    isProFeaturesLocked={isProFeaturesLocked}
                    selectedMoodName={this.props.selectedMoodName}
                    trackName={this.props.trackName}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                    onApplyMood={(mood) => this.runMood(mood)}
                    onClearMood={() => this.runClearMood()}
                    onOpen={(metadata) => this.props.onMoodOpen(metadata)}
                    onLockedClick={(metadata) =>
                      this.props.onMoodLockedClick(metadata)
                    }
                  />
                )}

                {openSheet === "skin" && (
                  <SkinSheet
                    open
                    skin={skin}
                    isProFeaturesLocked={isProFeaturesLocked}
                    debugMode={debugModeEnabled}
                    onSkinChange={(value) => {
                      this.props.onSkinChange(value);
                      this.setSheetOpen(null);
                    }}
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                  />
                )}

                {openSheet === "help" && (
                  <HelpSheet
                    open
                    isProFeaturesLocked={isProFeaturesLocked}
                    preservePitchMode={this.props.preservePitchMode}
                    speedUpMode={this.props.speedUpMode}
                    onSpeedUpModeChange={(value) =>
                      this.props.onSpeedUpModeChange(value)
                    }
                    onUpgradeClick={() => this.props.onUpgradeClick()}
                    onPreservePitchModeChange={(value) =>
                      this.props.onPreservePitchModeChange(value)
                    }
                    detachedPanel={this.props.detachedPanel}
                    onDetachedPanelChange={(value) =>
                      this.props.onDetachedPanelChange(value)
                    }
                    onResetClick={() => {
                      this.props.onResetControls();
                      this.setSheetOpen(null);
                    }}
                    onShortcutsClick={() => {
                      this.props.onCustomizeShortcut();
                      this.setSheetOpen(null);
                    }}
                    onChangelogClick={() => {
                      this.props.onChangelogClick();
                      this.setSheetOpen(null);
                    }}
                  />
                )}

                {openSheet === "feedback" && (
                  <FeedbackSheet
                    open
                    isFeedbackEnabled={isFeedbackEnabled}
                    isRateAppEnabled={isRateAppEnabled}
                    onFeedbackClick={(type) => {
                      this.props.onFeedbackClick(type);
                      this.setSheetOpen(null);
                    }}
                    onFaqClick={() => {
                      this.props.onHelpClick();
                      this.setSheetOpen(null);
                    }}
                    onRateAppClick={() => {
                      this.props.onRateAppClick();
                      this.setSheetOpen(null);
                    }}
                  />
                )}
                </div>
              </div>
            </div>

            <ActionsFooter
              onMoodClick={() =>
                this.setSheetOpen(openSheet === "mood" ? null : "mood")
              }
              onSkinClick={() =>
                this.setSheetOpen(openSheet === "skin" ? null : "skin")
              }
              onFeedbackClick={() =>
                this.setSheetOpen(openSheet === "feedback" ? null : "feedback")
              }
              onHelpClick={() =>
                this.setSheetOpen(openSheet === "help" ? null : "help")
              }
            />
          </div>
        </div>

          {debugModeEnabled && (
            <DebugBar
              testMode={this.props.testMode}
              paymentHookId={this.props.paymentHookId}
              hookBusy={this.props.hookBusy}
              hookError={this.props.hookError}
              isPro={isPro}
              trialMode={trialMode}
              usageHit={this.props.usageHit}
              usageProgress={this.props.usageProgress}
              onTestModeChange={(value) => this.props.onTestModeChange(value)}
              onRefreshPaymentHookClick={() =>
                this.props.onRefreshPaymentHookClick()
              }
              onStartTrialModeClick={() => this.props.onStartTrialModeClick()}
              onUsingUsageClick={() => this.props.onUsingUsageClick()}
              onForceMaxUsageClick={() => this.props.onForceMaxUsageClick()}
              onUpgradeClick={() => this.props.onUpgradeClick()}
            />
          )}
      </div>
    );
  }
}

PopupComponent.defaultProps = popupComponentDefaultProps;

self.Slowed.Components.Popup = PopupComponent;

export default PopupComponent;
